import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { ButtonCustom, ButtonOutLine } from 'app/components/elements/ButtonCustom';
import { InputSelect } from 'app/components/elements/InputSelect';
import { useFlash } from 'contexts/flash';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { Stack, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import React, { useEffect, useMemo, useState } from 'react';
import { getLocations, updateLocationParent } from 'services/locations';
import { Location, ParamGetLocations } from 'types/loctions';
import { isRoomLocationLevel, renderTitle } from 'utils/renderTitleLocation';

type FlatRow = Location & { parentRef: number | null };

function nid(x: unknown): string {
  return String(x);
}

function flattenTree(nodes: Location[], parentRef: number | null): FlatRow[] {
  const out: FlatRow[] = [];
  for (const n of nodes) {
    out.push({ ...n, parentRef });
    if (n.children?.length) {
      out.push(...flattenTree(n.children, n.id));
    }
  }
  return out;
}

function descendantIds(flat: FlatRow[], rootId: unknown): Set<string> {
  const byParent = new Map<string, string[]>();
  flat.forEach((row) => {
    const pk = row.parentRef == null ? '__root__' : nid(row.parentRef);
    const id = nid(row.id);
    if (!byParent.has(pk)) byParent.set(pk, []);
    byParent.get(pk)!.push(id);
  });
  const acc = new Set<string>();
  const stack = [nid(rootId)];
  while (stack.length) {
    const id = stack.pop()!;
    if (acc.has(id)) continue;
    acc.add(id);
    for (const c of byParent.get(id) || []) {
      stack.push(c);
    }
  }
  return acc;
}

function levelAfterShift(level: number, delta: number): number {
  const L = Number(level);
  return isRoomLocationLevel(L) ? L : L + delta;
}

function subtreeAllowsDelta(banned: Set<string>, flat: FlatRow[], delta: number): boolean {
  const rows = flat.filter((r) => banned.has(nid(r.id)));
  for (const r of rows) {
    const nl = levelAfterShift(Number(r.level), delta);
    if (nl < 1 || nl > 5) return false;
  }
  return true;
}

function canMoveUnderClientRoot(source: Location, banned: Set<string>, flat: FlatRow[]): boolean {
  if (isRoomLocationLevel(source.level)) {
    const rows = flat.filter((r) => banned.has(nid(r.id)));
    if (rows.length > 1) return false;
    return true;
  }
  const newRootLevel = 1;
  const delta = newRootLevel - Number(source.level);
  return subtreeAllowsDelta(banned, flat, delta);
}

function canMoveUnderParent(
  source: Location,
  parent: Location,
  banned: Set<string>,
  flat: FlatRow[]
): boolean {
  if (isRoomLocationLevel(source.level)) {
    const pl = Number(parent.level);
    return pl >= 1 && pl <= 4;
  }
  const newRootLevel = Number(parent.level) + 1;
  const delta = newRootLevel - Number(source.level);
  return subtreeAllowsDelta(banned, flat, delta);
}

function toSelectOptions(rows: FlatRow[]) {
  return rows.map((row) => {
    const t = renderTitle(Number(row.level));
    return { label: `${row.name} — ${t.title}`, value: String(row.id) };
  });
}

type Props = {
  open: boolean;
  location: Location | null;
  clientId: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const ModalReparentLocation = ({ open, location, clientId, onClose, onSuccess }: Props) => {
  const { setFlash } = useFlash();
  const [flat, setFlat] = useState<FlatRow[]>([]);
  const [path, setPath] = useState<Location[]>([]);
  const [deeperValue, setDeeperValue] = useState<string>('');

  useEffect(() => {
    if (!open || !clientId) return;
    void (async () => {
      try {
        const data = await getLocations({
          clientId,
          size: 500,
          page: 1,
          searchType: 'partial-text'
        } as Partial<ParamGetLocations>);
        const tree = (data.locations || []) as Location[];
        setFlat(flattenTree(tree, null));
      } catch {
        setFlat([]);
      }
    })();
  }, [open, clientId]);

  useEffect(() => {
    if (open) {
      setPath([]);
      setDeeperValue('');
    }
  }, [open, location?.id]);

  const banned = useMemo(
    () => (location ? descendantIds(flat, location.id) : new Set<string>()),
    [flat, location]
  );

  const rootRows = useMemo(() => {
    return flat.filter((r) => r.parentRef === null && !banned.has(nid(r.id)));
  }, [flat, banned]);

  const rootOptions = useMemo(() => toSelectOptions(rootRows), [rootRows]);

  const parentCandidate = path.length ? path[path.length - 1] : null;

  const deeperRows = useMemo(() => {
    if (!parentCandidate) return [];
    let rows = flat.filter(
      (r) => nid(r.parentRef) === nid(parentCandidate.id) && !banned.has(nid(r.id))
    );
    if (isRoomLocationLevel(location?.level)) {
      rows = rows.filter((r) => Number(r.level) <= 4);
    }
    return rows;
  }, [flat, parentCandidate, banned, location?.level]);

  const deeperOptions = useMemo(() => toSelectOptions(deeperRows), [deeperRows]);

  const showClientRoot = location ? canMoveUnderClientRoot(location, banned, flat) : false;
  const canConfirmParent =
    parentCandidate && location
      ? canMoveUnderParent(location, parentCandidate, banned, flat)
      : false;

  const { asyncCallback: asyncMove, isLoading: busy } = useAsyncCallback(
    async (parentId: number | null) => {
      if (!location) return;
      const cur = location.parentId ?? null;
      if (parentId === cur) {
        onClose();
        return;
      }
      await updateLocationParent(location.id, parentId);
      setFlash({ type: 'success', message: 'Location parent updated' });
      onSuccess();
      onClose();
    },
    [location, onClose, onSuccess, setFlash]
  );

  if (!location) return null;

  const L = Number(location.level);

  return (
    <DialogConfirm
      isOpen={open}
      onClose={onClose}
      isIconClose
      textTitle="Change parent location"
      textCancel="Close"
      maxWidth={600}>
      <Stack width="100%" gap={2} alignItems="stretch">
        <Typography fontSize={16} color="#333">
          Move <strong>{location.name}</strong> ({renderTitle(L).title}). Only this client&apos;s
          tree is shown. From <strong>{renderTitle(1).title}</strong> through{' '}
          <strong>{renderTitle(4).title}</strong>, the new tier is{' '}
          <strong>one step under the parent</strong> (whole subtree shifts; max depth 5).{' '}
          <strong>{renderTitle(5).title}</strong> stays at tier 5: either under{' '}
          <strong>{renderTitle(1).title}</strong>–<strong>{renderTitle(4).title}</strong>, or{' '}
          <strong>at client root</strong> (same as adding a room from the client — parent cleared,
          level still 5). Not under another <strong>{renderTitle(5).title}</strong>.
        </Typography>

        {showClientRoot && (
          <ButtonOutLine
            label={
              busy
                ? '…'
                : isRoomLocationLevel(location.level)
                  ? `Attach at client root — keep ${renderTitle(5).title} (level 5)`
                  : `Move under client root (${renderTitle(1).title})`
            }
            disabled={busy}
            onClick={() => void asyncMove(null)}
            sx={{ height: 48 }}
          />
        )}

        {path.length === 0 && (
          <InputSelect
            title={`Step 1 — choose a ${renderTitle(1).title}`}
            placeholder="Select A, B, C…"
            value=""
            onChange={(e: SelectChangeEvent<unknown>) => {
              const id = Number(e.target.value);
              const row = rootRows.find((r) => Number(r.id) === id);
              if (row) setPath([row]);
            }}
            options={rootOptions}
          />
        )}

        {path.length > 0 && (
          <>
            <Typography fontSize={15} color="#226AAA" fontWeight={600}>
              Selected path: {path.map((p) => p.name).join(' → ')}
            </Typography>

            <Stack direction="row" gap={2} flexWrap="wrap">
              <ButtonOutLine
                label="Back"
                disabled={busy}
                onClick={() => {
                  setPath((p) => p.slice(0, -1));
                  setDeeperValue('');
                }}
                sx={{ height: 48, minWidth: 120 }}
              />
              <ButtonCustom
                label={
                  busy
                    ? '…'
                    : `Move under “${parentCandidate!.name}” (${
                        renderTitle(Number(parentCandidate!.level)).title
                      })`
                }
                disabled={busy || !canConfirmParent}
                onClick={() => void asyncMove(parentCandidate!.id)}
                sx={{ height: 48, flex: 1, minWidth: 200 }}
              />
            </Stack>

            {!canConfirmParent && !isRoomLocationLevel(location.level) && (
              <Typography fontSize={13} color="#c62828">
                This parent would push part of the subtree past the maximum depth. Pick another
                parent or go higher in the tree.
              </Typography>
            )}
            {!canConfirmParent && isRoomLocationLevel(location.level) && parentCandidate && (
              <Typography fontSize={13} color="#c62828">
                {renderTitle(5).title} must be moved under {renderTitle(1).title}–
                {renderTitle(4).title} only.
              </Typography>
            )}

            {deeperRows.length > 0 && (
              <InputSelect
                key={path.map((p) => p.id).join('-')}
                title={`Optional — choose a deeper parent under “${parentCandidate!.name}”`}
                placeholder="Pick a deeper parent or confirm above"
                value={deeperValue}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  const v = String(e.target.value);
                  const id = Number(v);
                  const row = deeperRows.find((r) => Number(r.id) === id);
                  if (row) {
                    setPath((p) => [...p, row]);
                    setDeeperValue('');
                  }
                }}
                options={deeperOptions}
              />
            )}
          </>
        )}
      </Stack>
    </DialogConfirm>
  );
};
